import { useEditQuestionModal } from "features/Account/modules/Communications/modules/ContactForm/modules/QuestionForm/modules/EditQuestionForm/utils/useEditQuestionModal";
import { useSaveQuestionChangesCheck } from "features/Account/modules/Communications/modules/ContactForm/modules/QuestionForm/modules/SaveQuestionChanges/utils/useSaveQuestionChangesModal";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import { useManageOfferingsStore } from "features/ManageOfferings/store/useManageOfferingsStore";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useToggleState } from "utils/hooks";
import { filterBookingQuestions } from "./QuestionsPage.utils";
import { useQuestionForm } from "./useQuestionsForm";
import { useReorderBookingQuestions } from "../apis/reorderBookingQuestions";
import { useActionFieldModalWrapper } from "features/AdminPanel/modules/DraggableTableWrapper/modules/ActionsField/modules/ActionFieldsModalWrapper/utils/useActionFieldModalWrapper";
import { useGetBookingQuestions } from "../apis/getBookingQuestions";
import { contactFormColumnConfig } from "features/Account/modules/Communications/modules/ContactForm/modules/ContactFormQuestionsList/constants/ContactFormList.config";
import { questions_placement_mapping } from "constants/customerQuestions.constants";
import { useDeleteBookingQuestions } from "../apis/deleteBookingQuestions";
import { useEditBookingQuestions } from "../apis/editBookingQuestions";
import { useAddBookingQuestions } from "../apis/addBookingQuestions";

export const useBookedQuestions = () => {
  const [questionsModal, setQuestionsModal] = useState(false);
  const [isModalActive, showModal, hideModal] = useToggleState(false);
  const { listing_uuid } = useParams();

  const { confirmAndRun } = useSaveQuestionChangesCheck({
    skipCheck: !checkIsStringEmpty(listing_uuid),
  });
  const { isEditQuestionModalOpen } = useEditQuestionModal();
  const listingFormQuestionsList = useManageOfferingsStore(
    (state) => state.listingFormQuestionsList
  );
  const beforeBookingQuestions = filterBookingQuestions({
    data: listingFormQuestionsList,
    questionPlacement: questions_placement_mapping.before_booking,
  });

  const afterBookingQuestions = filterBookingQuestions({
    data: listingFormQuestionsList,
    questionPlacement: questions_placement_mapping.after_booking,
  });

  const {
    handleReordering,
    transformListingFormQuestionsList,
    handleDeleteQuestion,
    handleEditQuestion,
    handleAddQuestion,
    handleToggleQuestionMandatory,
  } = useQuestionForm({ listingUuid: listing_uuid });
  const { openEditQuestionmodal } = useEditQuestionModal();

  const { mutate: reorderBookingQuestionsAPI } = useReorderBookingQuestions();
  const { mutate: deleteBookingQuestionsAPI } = useDeleteBookingQuestions();
  const { mutate: editBookingQuestionsAPI } = useEditBookingQuestions();
  const { mutate: addBookingQuestionsAPI } = useAddBookingQuestions();
  const { isActionFieldsModalOpen, selectedRecord } =
    useActionFieldModalWrapper();

  const { isFetching } = useGetBookingQuestions({
    listingUuid: listing_uuid,
    onSuccess: (data) => {
      transformListingFormQuestionsList(data?.listing_questions ?? []);
    },
  });

  const handleAddBookingQuestion = async (formState) => {
    confirmAndRun((actionType) => {
      return handleAddQuestion({
        formState,
        addAPI: addBookingQuestionsAPI,
        onSuccess: () => {
          showModal();
          setQuestionsModal(false);
        },
        actionType,
      });
    });
  };

  const handleEditBookingQuestion = ({ formData, form, onSuccess }) => {
    confirmAndRun((actionType) => {
      return handleEditQuestion({
        formData,
        form,
        onSuccess,
        editAPI: editBookingQuestionsAPI,
        actionType,
      });
    });
  };

  const handleMarkMandatoryBookingQuestion = ({ questionId, value }) => {
    return confirmAndRun((actionType) => {
      return handleToggleQuestionMandatory({
        questionId,
        value,
        editAPI: editBookingQuestionsAPI,
        actionType,
      });
    });
  };

  const handleTableReordering = (items) =>
    confirmAndRun((actionType) => {
      return handleReordering({
        reorderedList: items,
        reorderAPI: reorderBookingQuestionsAPI,
        actionType,
      });
    });

  const handleDeleteBookingQuestion = ({ questionId }) => {
    confirmAndRun((actionType) => {
      return handleDeleteQuestion({
        questionId,
        deleteAPI: deleteBookingQuestionsAPI,
        actionType,
      });
    });
  };

  const onModalClose = () => {
    setQuestionsModal(false);
    hideModal();
  };

  const openModal = () => {
    setQuestionsModal(false);
    showModal();
  };

  const openQuestionsModal = () => {
    setQuestionsModal(true);
    hideModal();
  };

  const closeModal = () => {
    setQuestionsModal(false);
  };

  const columnsConfig = contactFormColumnConfig({
    handleDeleteQuestion: handleDeleteBookingQuestion,
    openEditQuestionmodal,
    handleToggleQuestionMandatory: handleMarkMandatoryBookingQuestion,
  });

  return {
    selectedRecord,
    isActionFieldsModalOpen,
    isFetching,
    handleAddBookingQuestion,
    handleEditBookingQuestion,
    handleMarkMandatoryBookingQuestion,
    handleTableReordering,
    handleDeleteBookingQuestion,
    onModalClose,
    openModal,
    openQuestionsModal,
    closeModal,
    columnsConfig,
    questionsModal,
    isModalActive,
    isEditQuestionModalOpen,
    beforeBookingQuestions,
    afterBookingQuestions,
    setQuestionsModal,
    openEditQuestionmodal,
    handleToggleQuestionMandatory,
  };
};
